'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@/entities/auth';
import { useLanguage } from '@/features/language';
import { useLenis } from '@/features/smooth-scroll';
import { ENV } from '@/shared/config';
import { getHostOrigin } from '@/shared/lib/path';
import { getImageProxyUrl } from '@/shared/lib/proxy-url';
import { login } from '@/shared/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import animations from '../animations.module.scss';
import styles from './SecSamplePreview.module.scss';
import { useDialogFocus } from '../../lib/useDialogFocus';
import { SampleCarouselControls } from '../components/sample-preview/SampleCarouselControls';
import { SampleListCarousel, type SampleListCarouselHandle } from '../components/sample-preview/SampleListCarousel';
import { SampleTopCloseButton } from '../components/sample-preview/SampleTopCloseButton';
import { TemplateStartButton } from '../components/sample-preview/TemplateStartButton';
import { SAMPLE_TEMPLATES } from '../components/sample-preview/samplePreviewData';
import type { SampleTemplate } from '../components/sample-preview/samplePreviewTypes';
type InlineMode = 'inlineInitial' | 'inlineReady' | 'inlineExited';
type OverlayMode = 'sampleOverlayList' | 'sampleOverlayDetail';
const INLINE_READY_END = 0.93;
const OVERLAY_FADE_MS = 680;
const OVERLAY_MOTION_MS = 980;
type CardFrame = {
  initial: {
    x: number;
    y: number;
    rotate: number;
    scale: number;
    opacity: number;
  };
  ready: {
    x: number;
    y: number;
    rotate: number;
    scale: number;
    opacity: number;
  };
  exited: {
    x: number;
    y: number;
    rotate: number;
    scale: number;
    opacity: number;
  };
};
function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
function lerp(from: number, to: number, progress: number) {
  return from + (to - from) * progress;
}
function easeOutCubic(progress: number) {
  const t = clamp(progress, 0, 1);
  return 1 - Math.pow(1 - t, 3);
}
function smoothstep(edge0: number, edge1: number, x: number) {
  const t = clamp((x - edge0) / Math.max(0.0001, edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}
function getScrollProgress(section: HTMLElement | null) {
  if (!section || typeof window === 'undefined') {
    return 0;
  }
  const rect = section.getBoundingClientRect();
  const viewportHeight = window.innerHeight || 1080;
  const enterAnchor = viewportHeight * 0.72;
  const totalScroll = Math.max(1, section.offsetHeight - viewportHeight + enterAnchor);
  const travelled = clamp(enterAnchor - rect.top, 0, totalScroll);
  return clamp(travelled / totalScroll, 0, 1);
}
function getInlineMode(progress: number, exitLock: boolean): InlineMode {
  if (exitLock) {
    return 'inlineReady';
  }
  if (progress < 0.2) {
    return 'inlineInitial';
  }
  if (progress < INLINE_READY_END) {
    return 'inlineReady';
  }
  return 'inlineReady';
}
const BACK_CARD_COLORS = ['#B7C7D4', '#C7CACC', '#2F3136', '#D7DADF'] as const;
const STAGE_FRAMES: CardFrame[] = [{
  initial: {
    x: 0,
    y: 28,
    rotate: 0,
    scale: 0.95,
    opacity: 0.98
  },
  ready: {
    x: 0,
    y: 0,
    rotate: 0,
    scale: 1,
    opacity: 1
  },
  exited: {
    x: 0,
    y: 28,
    rotate: 0,
    scale: 0.95,
    opacity: 0.98
  }
}, {
  initial: {
    x: -56,
    y: -12,
    rotate: -9,
    scale: 0.96,
    opacity: 1
  },
  ready: {
    x: -10,
    y: -3,
    rotate: -1.5,
    scale: 0.998,
    opacity: 1
  },
  exited: {
    x: -56,
    y: -12,
    rotate: -9,
    scale: 0.96,
    opacity: 1
  }
}, {
  initial: {
    x: -20,
    y: -4,
    rotate: -4,
    scale: 0.955,
    opacity: 1
  },
  ready: {
    x: -4,
    y: -1,
    rotate: -0.75,
    scale: 0.996,
    opacity: 1
  },
  exited: {
    x: -20,
    y: -4,
    rotate: -4,
    scale: 0.955,
    opacity: 1
  }
}, {
  initial: {
    x: 18,
    y: 4,
    rotate: 4,
    scale: 0.955,
    opacity: 1
  },
  ready: {
    x: 4,
    y: 1,
    rotate: 0.75,
    scale: 0.994,
    opacity: 1
  },
  exited: {
    x: 18,
    y: 4,
    rotate: 4,
    scale: 0.955,
    opacity: 1
  }
}, {
  initial: {
    x: 52,
    y: 12,
    rotate: 8,
    scale: 0.94,
    opacity: 1
  },
  ready: {
    x: 10,
    y: 3,
    rotate: 1.5,
    scale: 0.99,
    opacity: 1
  },
  exited: {
    x: 52,
    y: 12,
    rotate: 8,
    scale: 0.94,
    opacity: 1
  }
}];
type SampleInlineStackProps = {
  items: SampleTemplate[];
  progress: number;
  mode: InlineMode;
  interactive: boolean;
  onActivate: () => void;
};
function SampleInlineStack(t0) {
  const $ = _c(27);
  const {
    items,
    progress,
    mode,
    interactive,
    onActivate
  } = t0;
  const t = useTranslations("intro");
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== interactive || $[1] !== items || $[2] !== mode || $[3] !== onActivate || $[4] !== progress || $[5] !== t) {
    const cards = items.slice(0, 5);
    const initialProgress = clamp(progress / 0.2, 0, 1);
    const mergeProgress = smoothstep(0.58, 1, initialProgress);
    const readyProgress = mode === "inlineReady" ? 1 : 0;
    const exitProgress = mode === "inlineExited" ? easeOutCubic((progress - INLINE_READY_END) / (1 - INLINE_READY_END)) : 0;
    const translateY = mode === "inlineInitial" ? lerp(76, 0, initialProgress) : mode === "inlineReady" ? 0 : lerp(0, -18, exitProgress);
    const stackScale = mode === "inlineInitial" ? lerp(0.92, 1, initialProgress) : mode === "inlineReady" ? 1 : lerp(1, 0.985, exitProgress);
    t4 = "button";
    t5 = interactive ? onActivate : undefined;
    if ($[13] !== t) {
      t6 = t("sample.openAriaLabel");
      $[13] = t;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t7 = `${styles.stackButton} ${interactive ? styles.stackButtonInteractive : styles.stackButtonStatic}`;
    t1 = styles.stackFrame;
    const t8 = `translateY(${translateY}px) scale(${stackScale})`;
    if ($[15] !== t8) {
      t2 = {
        transform: t8,
        transition: "transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1)"
      };
      $[15] = t8;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    t3 = cards.map((item, index) => {
      const frame = STAGE_FRAMES[index];
      const frameProgress = mode === "inlineInitial" ? initialProgress : mode === "inlineReady" ? readyProgress : exitProgress;
      const fromFrame = mode === "inlineExited" ? frame.ready : frame.initial;
      const toFrame = mode === "inlineExited" ? frame.exited : frame.ready;
      const isFront = index === 0;
      const x = lerp(fromFrame.x, toFrame.x, frameProgress);
      const y = lerp(fromFrame.y, toFrame.y, frameProgress);
      const rotate = lerp(fromFrame.rotate, toFrame.rotate, frameProgress);
      const scale = lerp(fromFrame.scale, toFrame.scale, frameProgress);
      const opacity = isFront ? lerp(fromFrame.opacity, toFrame.opacity, frameProgress) : mode === "inlineInitial" ? lerp(1, 0, mergeProgress) : mode === "inlineReady" ? 0 : lerp(0, 1, exitProgress);
      const showGlow = isFront && (mode !== "inlineInitial" || initialProgress > 0.58);
      const cardClassName = [styles.stackCard, showGlow && mode === "inlineReady" ? animations.sampleCardGlow : ""].filter(Boolean).join(" ");
      return <div key={item.id} className={cardClassName} style={{
        zIndex: 20 - index,
        opacity,
        backgroundColor: isFront ? "transparent" : BACK_CARD_COLORS[index - 1],
        boxShadow: showGlow ? "0 0 106px 12px rgba(255,255,255,0.26)" : "none",
        transform: `translate3d(calc(-50% + ${x}px), ${y}px, 0) rotate(${rotate}deg) scale(${scale})`,
        transition: "transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 700ms ease"
      }}>{isFront ? <div className={styles.stackCardInner}><img src={getImageProxyUrl({
            src: item.thumbnailImage ?? "/intro/pdp_thum_1.png"
          })} alt={item.title} width={382} height={573} loading="lazy" decoding="async" className={styles.stackCardImage} /><div className={styles.stackCardOverlay} /></div> : null}</div>;
    });
    $[0] = interactive;
    $[1] = items;
    $[2] = mode;
    $[3] = onActivate;
    $[4] = progress;
    $[5] = t;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
  }
  let t8;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t8 = <div className={t1} style={t2}>{t3}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = <button type={t4} onClick={t5} aria-label={t6} className={t7}>{t8}</button>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
type SampleOverlayProps = {
  items: SampleTemplate[];
  selectedItem: SampleTemplate;
  mode: OverlayMode;
  onSelect: (item: SampleTemplate) => void;
  onCloseList: () => void;
  onCloseDetail: () => void;
  onStartTemplate: () => void;
  reveal: boolean;
  detailReveal: boolean;
};
function SampleOverlay(t0) {
  const $ = _c(71);
  const {
    items,
    selectedItem,
    mode,
    onSelect,
    onCloseList,
    onCloseDetail,
    onStartTemplate,
    reveal,
    detailReveal
  } = t0;
  const t = useTranslations("intro");
  const tCommon = useTranslations("common");
  const carouselRef = useRef(null);
  const rootRef = useRef(null);
  const isDetail = mode === "sampleOverlayDetail";
  const [overlaySettled, setOverlaySettled] = useState(false);
  const [detailSettled, setDetailSettled] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      open: true,
      containerRef: rootRef
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useDialogFocus(t1);
  let t2;
  let t3;
  if ($[1] !== reveal) {
    t2 = () => {
      if (!reveal) {
        setOverlaySettled(false);
        return;
      }
      const timer = window.setTimeout(() => {
        setOverlaySettled(true);
      }, OVERLAY_MOTION_MS + 120);
      return () => window.clearTimeout(timer);
    };
    t3 = [reveal];
    $[1] = reveal;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== detailReveal) {
    t4 = () => {
      if (!detailReveal) {
        setDetailSettled(false);
        return;
      }
      const timer_0 = window.setTimeout(() => {
        setDetailSettled(true);
      }, OVERLAY_MOTION_MS + 320);
      return () => window.clearTimeout(timer_0);
    };
    t5 = [detailReveal];
    $[4] = detailReveal;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const detailImage = selectedItem.fullDetailImage ?? selectedItem.previewImage ?? selectedItem.thumbnailImage ?? "/intro/pdp_1.png";
  const detailImageWidth = selectedItem.fullDetailImageWidth ?? 1720;
  const detailImageHeight = selectedItem.fullDetailImageHeight ?? 20942;
  let closeButtonClasses;
  if ($[7] !== isDetail) {
    closeButtonClasses = [styles.overlayCloseButton];
    if (isDetail) {
      closeButtonClasses.push(styles.overlayCloseButtonDetail);
    }
    $[7] = isDetail;
    $[8] = closeButtonClasses;
  } else {
    closeButtonClasses = $[8];
  }
  const closeButtonClassName = closeButtonClasses.join(" ");
  let handleCloseClick = onCloseList;
  if (isDetail) {
    handleCloseClick = onCloseDetail;
  }
  let overlayRootClasses;
  if ($[9] !== reveal) {
    overlayRootClasses = [styles.overlayRoot];
    if (reveal) {
      overlayRootClasses.push(styles.overlayRootVisible);
    }
    $[9] = reveal;
    $[10] = overlayRootClasses;
  } else {
    overlayRootClasses = $[10];
  }
  const overlayRootClassName = overlayRootClasses.join(" ");
  let overlayBodyClasses;
  if ($[11] !== overlaySettled || $[12] !== reveal) {
    overlayBodyClasses = [styles.overlayBody];
    if (reveal) {
      overlayBodyClasses.push(styles.overlayBodyVisible);
    }
    if (overlaySettled) {
      overlayBodyClasses.push(styles.overlayBodySettled);
    }
    $[11] = overlaySettled;
    $[12] = reveal;
    $[13] = overlayBodyClasses;
  } else {
    overlayBodyClasses = $[13];
  }
  const overlayBodyClassName = overlayBodyClasses.join(" ");
  let detailMainClasses;
  if ($[14] !== detailReveal || $[15] !== detailSettled) {
    detailMainClasses = [styles.detailMain];
    if (detailReveal) {
      detailMainClasses.push(styles.detailMainVisible);
    }
    if (detailSettled) {
      detailMainClasses.push(styles.detailMainSettled);
    }
    $[14] = detailReveal;
    $[15] = detailSettled;
    $[16] = detailMainClasses;
  } else {
    detailMainClasses = $[16];
  }
  const detailMainClassName = detailMainClasses.join(" ");
  let detailScrollClasses;
  if ($[17] !== detailReveal || $[18] !== detailSettled) {
    detailScrollClasses = [styles.detailScroll];
    if (detailReveal) {
      detailScrollClasses.push(styles.detailScrollVisible);
    }
    if (detailSettled) {
      detailScrollClasses.push(styles.detailScrollSettled);
    }
    $[17] = detailReveal;
    $[18] = detailSettled;
    $[19] = detailScrollClasses;
  } else {
    detailScrollClasses = $[19];
  }
  const detailScrollClassName = detailScrollClasses.join(" ");
  let detailDesktopCtaClasses;
  if ($[20] !== detailReveal) {
    detailDesktopCtaClasses = [styles.detailDesktopCta];
    if (detailReveal) {
      detailDesktopCtaClasses.push(styles.detailDesktopCtaVisible);
    }
    $[20] = detailReveal;
    $[21] = detailDesktopCtaClasses;
  } else {
    detailDesktopCtaClasses = $[21];
  }
  const detailDesktopCtaClassName = detailDesktopCtaClasses.join(" ");
  let detailDesktopCtaHolderClasses;
  if ($[22] !== detailReveal || $[23] !== detailSettled) {
    detailDesktopCtaHolderClasses = [styles.detailDesktopCtaHolder];
    if (detailReveal) {
      detailDesktopCtaHolderClasses.push(styles.detailDesktopCtaHolderVisible);
    }
    if (detailSettled) {
      detailDesktopCtaHolderClasses.push(styles.detailDesktopCtaHolderSettled);
    }
    $[22] = detailReveal;
    $[23] = detailSettled;
    $[24] = detailDesktopCtaHolderClasses;
  } else {
    detailDesktopCtaHolderClasses = $[24];
  }
  const detailDesktopCtaHolderClassName = detailDesktopCtaHolderClasses.join(" ");
  let detailMobileCtaClasses;
  if ($[25] !== detailReveal || $[26] !== detailSettled) {
    detailMobileCtaClasses = [styles.detailMobileCta];
    if (detailReveal) {
      detailMobileCtaClasses.push(styles.detailMobileCtaVisible);
    }
    if (detailSettled) {
      detailMobileCtaClasses.push(styles.detailMobileCtaSettled);
    }
    $[25] = detailReveal;
    $[26] = detailSettled;
    $[27] = detailMobileCtaClasses;
  } else {
    detailMobileCtaClasses = $[27];
  }
  const detailMobileCtaClassName = detailMobileCtaClasses.join(" ");
  let overlayModeAttr = "list";
  if (isDetail) {
    overlayModeAttr = "detail";
  }
  let t6;
  if ($[28] !== t) {
    t6 = t("sample.title");
    $[28] = t;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const t7 = overlayModeAttr;
  const t8 = handleCloseClick;
  let t9;
  if ($[30] !== tCommon) {
    t9 = tCommon("actions.close");
    $[30] = tCommon;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== handleCloseClick || $[33] !== t9) {
    t10 = <SampleTopCloseButton onClick={t8} label={t9} />;
    $[32] = handleCloseClick;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== closeButtonClassName || $[36] !== t10) {
    t11 = <div className={closeButtonClassName}>{t10}</div>;
    $[35] = closeButtonClassName;
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t) {
    t12 = t("sample.title");
    $[38] = t;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t12) {
    t13 = <header className={styles.overlayHeader}><p className={styles.overlayTitle}>{t12}</p></header>;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== isDetail || $[43] !== items || $[44] !== onSelect) {
    t14 = <div className={styles.overlayCarousel}><SampleListCarousel ref={carouselRef} items={items} onSelect={onSelect} paused={isDetail} /></div>;
    $[42] = isDetail;
    $[43] = items;
    $[44] = onSelect;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== overlayBodyClassName || $[47] !== t13 || $[48] !== t14) {
    t15 = <div className={overlayBodyClassName}>{t13}{t14}</div>;
    $[46] = overlayBodyClassName;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className={styles.overlayControls}><SampleCarouselControls onPrev={() => carouselRef.current?.prev()} onNext={() => carouselRef.current?.next()} size="sm" /></div>;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== detailDesktopCtaClassName || $[52] !== detailDesktopCtaHolderClassName || $[53] !== detailImage || $[54] !== detailImageHeight || $[55] !== detailImageWidth || $[56] !== detailMainClassName || $[57] !== detailMobileCtaClassName || $[58] !== detailScrollClassName || $[59] !== isDetail || $[60] !== onStartTemplate || $[61] !== selectedItem.title || $[62] !== t) {
    t17 = isDetail ? <><div className={styles.detailDarken} aria-hidden="true" /><div className={detailMainClassName}><div className={styles.detailImageWrap}><div className={styles.detailImageInner}><div className={detailScrollClassName} data-lenis-prevent={true}><img src={getImageProxyUrl({
                src: detailImage
              })} alt={selectedItem.title} className={styles.detailImage} width={detailImageWidth} height={detailImageHeight} loading="lazy" decoding="async" /></div></div><div className={detailDesktopCtaClassName}><div className={detailDesktopCtaHolderClassName}><TemplateStartButton onClick={onStartTemplate} label={t("sample.startTemplate")} /></div></div></div><div className={detailMobileCtaClassName}><TemplateStartButton onClick={onStartTemplate} label={t("sample.startTemplate")} /></div></div></> : null;
    $[51] = detailDesktopCtaClassName;
    $[52] = detailDesktopCtaHolderClassName;
    $[53] = detailImage;
    $[54] = detailImageHeight;
    $[55] = detailImageWidth;
    $[56] = detailMainClassName;
    $[57] = detailMobileCtaClassName;
    $[58] = detailScrollClassName;
    $[59] = isDetail;
    $[60] = onStartTemplate;
    $[61] = selectedItem.title;
    $[62] = t;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== overlayModeAttr || $[65] !== overlayRootClassName || $[66] !== t11 || $[67] !== t15 || $[68] !== t17 || $[69] !== t6) {
    t18 = <div ref={rootRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t6} className={overlayRootClassName} data-mode={t7}>{t11}{t15}{t16}{t17}</div>;
    $[64] = overlayModeAttr;
    $[65] = overlayRootClassName;
    $[66] = t11;
    $[67] = t15;
    $[68] = t17;
    $[69] = t6;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  return t18;
}
export const SecSamplePreview = () => {
  const t = useTranslations('intro');
  const router = useRouter();
  const [locale] = useLanguage();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const sectionRef = useRef<HTMLElement | null>(null);
  const lenis = useLenis();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [exitLock, setExitLock] = useState(false);
  const [overlayMode, setOverlayMode] = useState<OverlayMode | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(SAMPLE_TEMPLATES[0].id);
  const [overlayReveal, setOverlayReveal] = useState(false);
  const [detailReveal, setDetailReveal] = useState(false);
  const [overlayClosing, setOverlayClosing] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const returnScrollTopRef = useRef(0);
  const restoreScrollPendingRef = useRef(false);
  const previousOverlayModeRef = useRef<OverlayMode | null>(null);
  const selectedTemplate = useMemo(() => SAMPLE_TEMPLATES.find(item => item.id === selectedTemplateId) ?? SAMPLE_TEMPLATES[0], [selectedTemplateId]);
  const inlineMode = getInlineMode(scrollProgress, exitLock);
  const isOverlayOpen = overlayMode !== null || overlayClosing;
  const inlineInteractive = scrollProgress >= 0.1 && inlineMode === 'inlineReady';
  useEffect(() => {
    const previousOverlayMode = previousOverlayModeRef.current;
    previousOverlayModeRef.current = overlayMode;
    if (!overlayMode) {
      setOverlayReveal(false);
      setDetailReveal(false);
      setOverlayClosing(false);
      return;
    }
    if (previousOverlayMode !== null) {
      setOverlayReveal(true);
      if (overlayMode === 'sampleOverlayDetail') {
        setDetailReveal(false);
        const frame = window.requestAnimationFrame(() => {
          setDetailReveal(true);
        });
        return () => window.cancelAnimationFrame(frame);
      }
      return;
    }
    setOverlayReveal(false);
    const frame_0 = window.requestAnimationFrame(() => {
      setOverlayReveal(true);
    });
    return () => window.cancelAnimationFrame(frame_0);
  }, [overlayMode]);
  const closeToLanding = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }
    setOverlayReveal(false);
    setOverlayClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      setOverlayMode(null);
      setOverlayClosing(false);
      closeTimerRef.current = null;
    }, OVERLAY_FADE_MS + 90);
    setExitLock(false);
  }, []);
  useEffect(() => {
    let lastProgress = -1;
    const update = () => {
      const next = getScrollProgress(sectionRef.current);
      if (Math.abs(next - lastProgress) < 0.002) {
        return;
      }
      lastProgress = next;
      setScrollProgress(next);
    };
    update();
    if (lenis) {
      lenis.on('scroll', update);
      return () => lenis.off('scroll', update);
    }
    let rafId = window.requestAnimationFrame(function tick() {
      update();
      rafId = window.requestAnimationFrame(tick);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [lenis]);
  useEffect(() => {
    if (overlayMode !== null || !restoreScrollPendingRef.current) {
      return;
    }
    const targetTop = returnScrollTopRef.current;
    restoreScrollPendingRef.current = false;
    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        window.scrollTo({
          top: targetTop,
          behavior: 'auto'
        });
      });
      return () => window.cancelAnimationFrame(secondFrame);
    });
    return () => window.cancelAnimationFrame(firstFrame);
  }, [overlayMode]);
  useEffect(() => {
    const scrollY = window.scrollY;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyLeft = document.body.style.left;
    const previousBodyRight = document.body.style.right;
    const previousBodyWidth = document.body.style.width;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlOverscroll = document.documentElement.style.overscrollBehavior;
    if (isOverlayOpen) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'none';
    } else {
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      document.body.style.width = previousBodyWidth;
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.overscrollBehavior = previousHtmlOverscroll;
    }
    return () => {
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.left = previousBodyLeft;
      document.body.style.right = previousBodyRight;
      document.body.style.width = previousBodyWidth;
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.overscrollBehavior = previousHtmlOverscroll;
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, [isOverlayOpen]);
  useEffect(() => {
    if (inlineMode !== 'inlineExited' && scrollProgress < 0.16) {
      setExitLock(false);
    }
  }, [inlineMode, scrollProgress]);
  useEffect(() => {
    if (!overlayMode) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (overlayMode === 'sampleOverlayDetail') {
        setOverlayMode('sampleOverlayList');
        return;
      }
      closeToLanding();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeToLanding, overlayMode]);
  const handleActivate = useCallback(() => {
    if (scrollProgress < 0.1 || inlineMode !== 'inlineReady') {
      return;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    returnScrollTopRef.current = window.scrollY;
    restoreScrollPendingRef.current = true;
    setOverlayClosing(false);
    setOverlayMode('sampleOverlayList');
  }, [inlineMode, scrollProgress]);
  const handleCloseList = useCallback(() => {
    closeToLanding();
  }, [closeToLanding]);
  const handleCloseDetail = useCallback(() => {
    setOverlayMode('sampleOverlayList');
  }, []);
  const handleSelectTemplate = useCallback((item_0: SampleTemplate) => {
    setSelectedTemplateId(item_0.id);
    setDetailReveal(false);
    setOverlayMode('sampleOverlayDetail');
  }, []);
  const handleStartTemplate = useCallback(() => {
    // 로그인 상태면 products 의 상세페이지 생성 모달을 바로 띄우고,
    // 비로그인 상태면 로그인 후 같은 위치로 복귀시킨다.
    const detailCreatePath = `/${locale}/products?modal=generate-product&type=detail`;
    if (isAuthenticated) {
      router.push(detailCreatePath);
      return;
    }
    login(ENV.AUTH_API_BASE_URL, `${getHostOrigin()}${detailCreatePath}`);
  }, [isAuthenticated, router, locale]);
  const overlay = overlayMode ? <SampleOverlay items={SAMPLE_TEMPLATES} selectedItem={selectedTemplate} mode={overlayMode} onSelect={handleSelectTemplate} onCloseList={handleCloseList} onCloseDetail={handleCloseDetail} onStartTemplate={handleStartTemplate} reveal={overlayReveal} detailReveal={detailReveal} /> : null;
  const stickyClasses = [styles.sticky];
  if (isOverlayOpen) {
    stickyClasses.push(styles.stickyHidden);
  }
  const stickyClassName = stickyClasses.join(' ');
  return <section ref={sectionRef} id="sample-preview" data-section="sample-preview" className={styles.section}>
      <div aria-hidden="true" className={styles.bgWrap}>
        <div className={styles.bgGradient} />
      </div>

      <div className={stickyClassName} aria-hidden={isOverlayOpen}>
        <div className={styles.inner}>
          <header className={styles.header}>
            <p className={styles.title}>{t('sample.title')}</p>
          </header>

          <div className={styles.stackArea}>
            <div className={styles.stackWrap}>
              <div className={styles.stackContainer}>
                <SampleInlineStack items={SAMPLE_TEMPLATES} progress={scrollProgress} mode={inlineMode} interactive={inlineInteractive} onActivate={handleActivate} />
              </div>

              <div aria-hidden="true" className={`${styles.pulseGlow} ${inlineMode === 'inlineReady' ? animations.pulseGlowActive : ''}`} />
            </div>
          </div>
        </div>
      </div>

      {typeof document !== 'undefined' && overlay ? createPortal(overlay, document.body) : null}
    </section>;
};